import { CASES_PRIORITY_COLORS } from '@@/ui/theme/colors';
import type { CaseResponse, ProfileResponse } from '../types/api.types';
import { CaseRecord } from '../types';
import { formatDate } from 'date-fns';

export const getPriorityBadgeClasses = (priority: string): string => {
  const priorityLower = priority?.toLowerCase() || '';
  const priorityConfig =
    CASES_PRIORITY_COLORS[priorityLower as keyof typeof CASES_PRIORITY_COLORS] || CASES_PRIORITY_COLORS.low;

  return `${priorityConfig.bg} ${priorityConfig.text} border ${priorityConfig.border}`;
};

// Type for user attributes from useGetUserRolePermissions hook
type UserAttributes = {
  email: string;
  id?: string;
  companyName?: string;
};

// Extended type for API fields with custom properties
type ExtendedCaseFields = CaseResponse['fields'] & {
  case_type?: string;
  prnx_queue_name?: string;
  prnx_agent_name?: string;
  customerProfile?: ProfileResponse;
};

// Normalize data from API response (CaseResponse.fields)
export const normalizeApiData = (fields: ExtendedCaseFields, userAttributes: UserAttributes) => ({
  title: (fields.title as string) || '',
  status: (fields.status as string) || 'Open',
  priority: (fields.priority as string) || 'Low',
  summary: (fields.summary as string) || '',
  customerEmail: fields?.customerProfile?.profile?.EmailAddress || userAttributes.email,
  assignedQueue: (fields.assignedQueueArn as string) || '',
  assignedUser: (fields.assignedUserArn as string) || '',
  caseType: (fields.caseType as string) || 'Bug Report',
  assignedQueueName: (fields.assignedQueue as string) || '',
  assignedAgentName: (fields.assignedUser as string) || '',
  ccEmails: (fields.ccEmails as string) || ''
});

export function transformedCases(cases: CaseResponse[]): CaseRecord[] {
  return cases.map((apiCase: CaseResponse) => {
    const createdDateValue = apiCase.fields?.['Date/Time Opened'] as string | undefined;
    const updatedDateValue = apiCase.fields?.['Date/Time Updated'] as string | undefined;

    const createdDateObj = createdDateValue ? new Date(createdDateValue) : new Date();
    const updatedDateObj = updatedDateValue ? new Date(updatedDateValue) : new Date();

    // Check if dates are valid
    const isCreatedDateValid = !isNaN(createdDateObj.getTime());
    const isUpdatedDateValid = !isNaN(updatedDateObj.getTime());

    return {
      caseId: String(apiCase.case_id || ''),
      status: String(apiCase.fields?.Status || '—'),
      referenceNumber: String(apiCase.fields?.['Reference Number'] || '—'),
      title: String(apiCase.fields?.Title || '—'),
      summary: String(apiCase.fields?.Summary || '—'),
      assignedQueue: String(apiCase.fields?.prnx_queue_name || '—'),
      assignedUser: String(apiCase.fields?.prnx_agent_name || '—'),
      company: String(apiCase.fields?.['Company Name'] || '—'),
      priority: String(apiCase.fields?.Priority || '—'),
      created: isCreatedDateValid
        ? formatDate(createdDateObj, 'MMM d yyyy hh:mm a').replace(/AM|PM/, (match) => match.toLowerCase())
        : '—',
      createdTs: isCreatedDateValid ? createdDateObj.getTime() : 0,
      updated: isUpdatedDateValid
        ? formatDate(updatedDateObj, 'MMM d yyyy hh:mm a').replace(/AM|PM/, (match) => match.toLowerCase())
        : '—',
      updatedTs: isUpdatedDateValid ? updatedDateObj.getTime() : 0,
      customerName: String(apiCase.fields?.['Reporter Name'] || '—'),
      customerId: String(apiCase.fields?.['Customer Id'] || '—'),
      assignedQueueArn: String(apiCase.fields?.['Assigned Queue'] || '—'),
      assignedUserArn: String(apiCase.fields?.['Assigned User'] || '—'),
      caseType: String(apiCase.fields?.['Case Type'] || '—'),
      companyId: String(apiCase.fields?.['Company Id'] || '—'),
      ccEmails: String(apiCase.fields?.['CC Email'] || '')
    };
  });
}

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      const base64Content = base64.split(',')[1];
      resolve(base64Content);
    };
    reader.onerror = (error) => reject(error);
  });
};
